import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, MapPinIcon, UserIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, FunnelIcon, CurrencyDollarIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import ContactSearchModal from './ContactSearchModal';
import PlotSearchModal from './PlotSearchModal';
import FlexFieldValues from './FlexFieldValues';
import DocumentGenerationPanel from './DocumentGenerationPanel';

// LocalStorage keys for sort persistence
const SALES_SORT_COLUMN_KEY = 'salesSortColumn';
const SALES_SORT_ORDER_KEY = 'salesSortOrder';

const SalesConfig = ({ previewMode = false }) => {
  const { token } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [salePlots, setSalePlots] = useState([]);
  const hasHandledUrlParams = useRef(false);

  // Pagination and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const [pageSize] = useState(10);
  const searchDebounceRef = useRef(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('');
  const [filterHasPlots, setFilterHasPlots] = useState('');
  const [filterProductId, setFilterProductId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterHasUnpaidBalance, setFilterHasUnpaidBalance] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Sort state with localStorage persistence
  const [sortColumn, setSortColumn] = useState(() => {
    return localStorage.getItem(SALES_SORT_COLUMN_KEY) || 'updatedAt';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem(SALES_SORT_ORDER_KEY) || 'DESC';
  });

  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    contactId: '',
    note: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentType: 'Cash'
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [salePayments, setSalePayments] = useState([]);
  const [isPaymentContactModalOpen, setIsPaymentContactModalOpen] = useState(false);
  const [selectedPaymentContact, setSelectedPaymentContact] = useState(null);
  const [formData, setFormData] = useState({
    contactId: '',
    dueDate: '',
    items: []
  });
  const [lineItem, setLineItem] = useState({
    productId: '',
    qty: 1,
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewingSale, setViewingSale] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isPlotModalOpen, setIsPlotModalOpen] = useState(false);
  const [isFormPlotModalOpen, setIsFormPlotModalOpen] = useState(false);
  const [formPlots, setFormPlots] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [pendingPaymentModal, setPendingPaymentModal] = useState(false);

  // Stripe invoice state
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceSuccess, setInvoiceSuccess] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [saleInvoices, setSaleInvoices] = useState([]);

  // Flex field states for create/edit form
  const [flexDefs, setFlexDefs] = useState([]);
  const [flexVals, setFlexVals] = useState({});

  // Fetch sales with pagination, search, filters, and sorting
  const fetchSales = useCallback(async (page = 1, search = '', filters = {}, sort = { column: 'updatedAt', order: 'DESC' }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        search: search,
        sortBy: sort.column,
        sortOrder: sort.order
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.hasPlots) params.append('hasPlots', filters.hasPlots);
      if (filters.productId) params.append('productId', filters.productId);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.hasUnpaidBalance) params.append('hasUnpaidBalance', filters.hasUnpaidBalance);

      const response = await fetch(`/api/sales?${params}`, {
        headers: { Authorization: token }
      });
      if (response.ok) {
        const data = await response.json();
        setSales(data.sales);
        setTotalPages(data.pagination.totalPages);
        setTotalSales(data.pagination.total);
        setCurrentPage(data.pagination.page);
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  }, [token, pageSize]);

  // Get current filters object
  const getCurrentFilters = useCallback(() => ({
    status: filterStatus,
    hasPlots: filterHasPlots,
    productId: filterProductId,
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
    hasUnpaidBalance: filterHasUnpaidBalance
  }), [filterStatus, filterHasPlots, filterProductId, filterDateFrom, filterDateTo, filterHasUnpaidBalance]);

  // Fetch reference data (contacts, products, statuses) only once
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [contactsRes, productsRes, statusesRes] = await Promise.all([
          fetch('/api/contacts?all=true', { headers: { Authorization: token } }),
          fetch('/api/products', { headers: { Authorization: token } }),
          fetch('/api/sales/statuses', { headers: { Authorization: token } })
        ]);
        if (contactsRes.ok) setContacts(await contactsRes.json());
        if (productsRes.ok) setProducts(await productsRes.json());
        if (statusesRes.ok) setStatuses(await statusesRes.json());
      } catch (error) {
        console.error('Error fetching reference data:', error);
      }
    };
    fetchReferenceData();
  }, [token]);

  // Check if Stripe payment gateway is enabled
  useEffect(() => {
    const checkStripeStatus = async () => {
      try {
        const response = await fetch('/api/stripe/status', {
          headers: { Authorization: token }
        });
        if (response.ok) {
          const data = await response.json();
          setStripeEnabled(data.enabled);
        }
      } catch (err) {
        // Stripe not available
      }
    };
    checkStripeStatus();
  }, [token]);

  // Fetch sales when page, filters, or sort changes
  useEffect(() => {
    fetchSales(currentPage, searchTerm, getCurrentFilters(), { column: sortColumn, order: sortOrder });
  }, [currentPage, searchTerm, filterStatus, filterHasPlots, filterProductId, filterDateFrom, filterDateTo, filterHasUnpaidBalance, sortColumn, sortOrder, fetchSales, getCurrentFilters]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchSales(1, searchTerm, getCurrentFilters(), { column: sortColumn, order: sortOrder });
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm, sortColumn, sortOrder, fetchSales, getCurrentFilters]);

  // Handle URL params to auto-open form with pre-selected contact or view/payment modals
  useEffect(() => {
    if (hasHandledUrlParams.current || contacts.length === 0) return;

    const action = searchParams.get('action');
    const contactId = searchParams.get('contactId');
    const saleId = searchParams.get('saleId');

    if (action === 'new') {
      hasHandledUrlParams.current = true;
      // Open form with pre-selected contact if provided
      setSelectedSale(null);
      const preselectedContactId = contactId || '';
      // Calculate default due date (one month from now)
      const defaultDueDate = new Date();
      defaultDueDate.setMonth(defaultDueDate.getMonth() + 1);
      setFormData({
        contactId: preselectedContactId,
        dueDate: defaultDueDate.toISOString().split('T')[0],
        items: []
      });
      // Set the selected contact for display
      if (preselectedContactId) {
        const contact = contacts.find(c => c.id === parseInt(preselectedContactId));
        setSelectedContact(contact || null);
      } else {
        setSelectedContact(null);
      }
      setIsFormOpen(true);
      setError('');
      setSearchParams({});
    } else if ((action === 'view' || action === 'payment') && saleId) {
      hasHandledUrlParams.current = true;
      // If action is payment, set the flag to open payment modal after sale loads
      if (action === 'payment') {
        setPendingPaymentModal(true);
      }
      // Fetch and view the sale
      const fetchAndViewSale = async () => {
        try {
          const response = await fetch(`/api/sales/${saleId}`, {
            headers: { Authorization: token }
          });
          if (response.ok) {
            const sale = await response.json();
            // Open the sale view modal
            handleViewSale(sale);
          }
        } catch (error) {
          console.error('Error fetching sale:', error);
        }
      };
      fetchAndViewSale();
      setSearchParams({});
    }
  }, [contacts, searchParams]);

  // Effect to open payment modal when viewingSale is set and pendingPaymentModal is true
  useEffect(() => {
    if (viewingSale && pendingPaymentModal) {
      setPendingPaymentModal(false);
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        handleOpenPaymentModal();
      }, 50);
    }
  }, [viewingSale, pendingPaymentModal]);

  // Refresh sales helper
  const refreshSales = () => {
    fetchSales(currentPage, searchTerm, getCurrentFilters(), { column: sortColumn, order: sortOrder });
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    let newOrder = 'DESC';
    if (sortColumn === column) {
      // Toggle order if clicking the same column
      newOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
    }
    setSortColumn(column);
    setSortOrder(newOrder);
    localStorage.setItem(SALES_SORT_COLUMN_KEY, column);
    localStorage.setItem(SALES_SORT_ORDER_KEY, newOrder);
    setCurrentPage(1);
  };

  // Render sort icon for column headers
  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortOrder === 'ASC' ? (
      <ChevronUpIcon className="h-4 w-4 ml-1 inline-block" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 ml-1 inline-block" />
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterStatus('');
    setFilterHasPlots('');
    setFilterProductId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterHasUnpaidBalance('');
    setCurrentPage(1);
  };

  // Count active filters
  const activeFilterCount = [filterStatus, filterHasPlots, filterProductId, filterDateFrom, filterDateTo, filterHasUnpaidBalance].filter(Boolean).length;

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const fetchFlexDefs = async (entityId) => {
    try {
      const response = await fetch('/api/flex-fields/definitions?entityType=sale', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) setFlexDefs(await response.json());
    } catch (err) { console.error('Error fetching flex defs:', err); }

    if (entityId) {
      try {
        const response = await fetch(`/api/flex-fields/values/sale/${entityId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const valMap = {};
          data.forEach(v => { valMap[v.definitionId] = v.value; });
          setFlexVals(valMap);
        }
      } catch (err) { console.error('Error fetching flex vals:', err); }
    } else {
      setFlexVals({});
    }
  };

  const handleOpenForm = (sale = null) => {
    if (sale) {
      setSelectedSale(sale);
      setFormData({
        contactId: sale.contactId || '',
        dueDate: sale.dueDate ? new Date(sale.dueDate).toISOString().split('T')[0] : '',
        items: sale.items || []
      });
      // Set the selected contact for display
      const contact = contacts.find(c => c.id === sale.contactId);
      setSelectedContact(contact || null);
      fetchFlexDefs(sale.id);
    } else {
      setSelectedSale(null);
      // Calculate default due date (one month from now)
      const defaultDueDate = new Date();
      defaultDueDate.setMonth(defaultDueDate.getMonth() + 1);
      setFormData({
        contactId: '',
        dueDate: defaultDueDate.toISOString().split('T')[0],
        items: []
      });
      setSelectedContact(null);
      fetchFlexDefs(null);
    }
    setFormPlots([]);
    setLineItem({ productId: '', qty: 1, note: '' });
    setIsFormOpen(true);
    setError('');
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedSale(null);
    setSelectedContact(null);
    setFormPlots([]);
    setError('');
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setFormData({ ...formData, contactId: contact.id });
  };

  // Handlers for plots in the new sale form
  const handleAddFormPlot = (plot) => {
    // Check if plot is already added
    if (formPlots.some(p => p.fid === plot.fid)) return;
    setFormPlots(prev => [...prev, plot]);
    setIsFormPlotModalOpen(false);
  };

  const handleRemoveFormPlot = (plotFid) => {
    setFormPlots(prev => prev.filter(p => p.fid !== plotFid));
  };

  const handleAddPlotToSale = async (plot) => {
    if (!viewingSale) return;

    try {
      const response = await fetch('/api/plot-sales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          plotId: plot.fid,
          saleId: viewingSale.id
        })
      });

      if (response.ok) {
        const newPlotSale = await response.json();
        setSalePlots(prev => [...prev, { ...newPlotSale, PlotPoly: plot }]);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to add plot to sale');
      }
    } catch (error) {
      console.error('Error adding plot to sale:', error);
      alert('Failed to add plot to sale');
    }
  };

  const handleRemovePlotFromSale = async (plotSaleId) => {
    try {
      const response = await fetch(`/api/plot-sales/${plotSaleId}`, {
        method: 'DELETE',
        headers: { Authorization: token }
      });

      if (response.ok) {
        setSalePlots(prev => prev.filter(ps => ps.id !== plotSaleId));
      } else {
        alert('Failed to remove plot from sale');
      }
    } catch (error) {
      console.error('Error removing plot from sale:', error);
    }
  };

  const handleAddItem = () => {
    const product = products.find(p => p.id === parseInt(lineItem.productId));
    if (!product || lineItem.qty <= 0) return;

    setFormData({
      ...formData,
      items: [...formData.items, {
        name: product.name,
        price: product.price,
        productStatus: product.status?.name,
        saleProductId: product.id,
        qty: lineItem.qty,
        note: lineItem.note
      }]
    });
    setLineItem({ productId: '', qty: 1, note: '' });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const saveFlexFieldValues = async (entityId) => {
    const hasValues = flexDefs.some(def => flexVals[def.id]);
    if (!hasValues) return;

    const valueArray = flexDefs.map(def => ({
      definitionId: def.id,
      value: flexVals[def.id] || ''
    }));

    try {
      await fetch(`/api/flex-fields/values/sale/${entityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ values: valueArray })
      });
    } catch (err) { console.error('Error saving flex values:', err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = selectedSale ? `/api/sales/${selectedSale.id}` : '/api/sales';
      const method = selectedSale ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          ...formData,
          dueDate: formData.dueDate || null
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Failed to save sale');
      }

      const savedSale = await response.json();

      // Save flex field values
      const entityId = selectedSale ? selectedSale.id : savedSale.id;
      await saveFlexFieldValues(entityId);

      // If creating a new sale and plots were selected, associate them
      if (!selectedSale && formPlots.length > 0) {
        for (const plot of formPlots) {
          try {
            await fetch('/api/plot-sales', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: token,
              },
              body: JSON.stringify({
                saleId: savedSale.id,
                plotId: plot.fid
              })
            });
          } catch (plotError) {
            console.error('Error associating plot:', plotError);
          }
        }
      }

      refreshSales();
      handleCloseForm();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, closeModal = false) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;

    try {
      const response = await fetch(`/api/sales/${id}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });

      if (response.ok) {
        refreshSales();
        if (closeModal) setViewingSale(null);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete sale');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete sale');
    }
  };

  const handleViewSale = async (sale) => {
    setViewingSale(sale);
    setSalePlots([]);
    setSalePayments([]);

    // Fetch associated plots and payments for this sale
    try {
      const [plotsRes, paymentsRes] = await Promise.all([
        fetch(`/api/plot-sales/sale/${sale.id}`, { headers: { Authorization: token } }),
        fetch(`/api/payments/sale/${sale.id}`, { headers: { Authorization: token } })
      ]);
      if (plotsRes.ok) {
        const plots = await plotsRes.json();
        setSalePlots(plots);
      }
      if (paymentsRes.ok) {
        const payments = await paymentsRes.json();
        setSalePayments(payments);
      }
    } catch (error) {
      console.error('Error fetching sale data:', error);
    }
  };

  const handleViewPlotOnMap = (plotId) => {
    navigate(`/plots?highlight=${plotId}`);
  };

  const handleEditFromView = () => {
    handleOpenForm(viewingSale);
    setViewingSale(null);
  };

  const formatStatusHistory = (sale) => {
    const history = [];
    try {
      // statusHistory may already be an object (from Sequelize JSON type) or a string
      let statusHistory = sale.statusHistory;
      if (typeof statusHistory === 'string') {
        statusHistory = JSON.parse(statusHistory || '[]');
      }
      if (!Array.isArray(statusHistory)) {
        statusHistory = [];
      }
      statusHistory.forEach(entry => {
        // Handle both new format (status string) and legacy format (statusId integer)
        let statusLabel;
        if (entry.status) {
          // New format - find label from statuses list
          const statusInfo = statuses.find(s => s.value === entry.status);
          statusLabel = statusInfo?.label || entry.status;
        } else if (entry.statusId !== undefined) {
          // Legacy format - look up by ID
          const statusInfo = statuses.find(s => s.id === entry.statusId);
          statusLabel = statusInfo?.name || statusInfo?.label || `Status ${entry.statusId}`;
        } else {
          statusLabel = 'Unknown';
        }
        history.push({
          status: statusLabel,
          date: new Date(entry.date),
          changedBy: entry.changedBy || null
        });
      });
    } catch (e) {
      console.error('Error parsing status history:', e);
    }
    return history;
  };

  const handleStatusChange = async (saleId, newStatus) => {
    try {
      const sale = sales.find(s => s.id === saleId);
      const response = await fetch(`/api/sales/${saleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ ...sale, status: newStatus }),
      });

      if (response.ok) {
        refreshSales();

        // If the modal is open for this sale, fetch updated data
        if (viewingSale && viewingSale.id === saleId) {
          const saleResponse = await fetch(`/api/sales/${saleId}`, {
            headers: { Authorization: token }
          });
          if (saleResponse.ok) {
            const updatedSale = await saleResponse.json();
            setViewingSale(updatedSale);
          }
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? `${contact.firstName} ${contact.lastName}` : '-';
  };

  const getStatusName = (status) => {
    const statusInfo = statuses.find(s => s.value === status);
    return statusInfo?.label || status || '-';
  };

  const calculateTotal = (items) => {
    return items?.reduce((sum, item) => sum + (item.price * item.qty), 0) || 0;
  };

  const calculateTotalPaid = (payments) => {
    return payments?.reduce((sum, payment) => sum + parseFloat(payment.amount), 0) || 0;
  };

  const calculateRemainingBalance = (items, payments) => {
    const total = calculateTotal(items);
    const paid = calculateTotalPaid(payments);
    return Math.max(0, total - paid);
  };

  // Payment modal handlers
  const handleOpenPaymentModal = () => {
    if (!viewingSale) return;

    // Default contact to the sale's contact
    const saleContact = contacts.find(c => c.id === viewingSale.contactId);
    setSelectedPaymentContact(saleContact || null);

    setPaymentFormData({
      amount: '',
      contactId: viewingSale.contactId || '',
      note: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentType: 'Cash'
    });
    setPaymentError('');
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setPaymentError('');
    setSelectedPaymentContact(null);
  };

  const handlePaymentContactSelect = (contact) => {
    setSelectedPaymentContact(contact);
    setPaymentFormData({ ...paymentFormData, contactId: contact.id });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!viewingSale) return;

    setPaymentLoading(true);
    setPaymentError('');

    try {
      const response = await fetch('/api/payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token
        },
        body: JSON.stringify({
          saleId: viewingSale.id,
          contactId: paymentFormData.contactId,
          amount: parseFloat(paymentFormData.amount),
          paymentDate: paymentFormData.paymentDate,
          paymentType: paymentFormData.paymentType,
          note: paymentFormData.note
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to record payment');
      }

      const newPayment = await response.json();
      setSalePayments(prev => [newPayment, ...prev]);
      handleClosePaymentModal();
      refreshSales(); // Refresh to update auto-calculated status changes

      // Fetch the updated sale to get the new status and statusHistory
      const saleResponse = await fetch(`/api/sales/${viewingSale.id}`, {
        headers: { Authorization: token }
      });
      if (saleResponse.ok) {
        const updatedSale = await saleResponse.json();
        setViewingSale(updatedSale);
      }
    } catch (error) {
      setPaymentError(error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;

    try {
      const response = await fetch(`/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { Authorization: token }
      });

      if (response.ok) {
        setSalePayments(prev => prev.filter(p => p.id !== paymentId));
        refreshSales();

        // Fetch the updated sale to get the new status and statusHistory
        if (viewingSale) {
          const saleResponse = await fetch(`/api/sales/${viewingSale.id}`, {
            headers: { Authorization: token }
          });
          if (saleResponse.ok) {
            const updatedSale = await saleResponse.json();
            setViewingSale(updatedSale);
          }
        }
      } else {
        alert('Failed to delete payment');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  // Stripe invoice handlers
  const handleSendPaymentInvoice = async () => {
    if (!viewingSale) return;
    setSendingInvoice(true);
    setInvoiceError('');
    setInvoiceSuccess('');

    try {
      const response = await fetch('/api/stripe/send-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token
        },
        body: JSON.stringify({ saleId: viewingSale.id })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setInvoiceSuccess('Invoice email sent successfully');
        // Reload invoices for this sale
        loadSaleInvoices(viewingSale.id);
      } else {
        setInvoiceError(data.error || 'Failed to send invoice');
      }
    } catch (err) {
      setInvoiceError('Failed to send invoice email');
    } finally {
      setSendingInvoice(false);
    }
  };

  const loadSaleInvoices = async (saleId) => {
    try {
      const response = await fetch(`/api/stripe/invoices/${saleId}`, {
        headers: { Authorization: token }
      });
      if (response.ok) {
        const data = await response.json();
        setSaleInvoices(data.invoices || []);
      }
    } catch (err) {
      // Not critical
    }
  };

  // Load invoices when viewing a sale
  useEffect(() => {
    if (viewingSale && stripeEnabled) {
      loadSaleInvoices(viewingSale.id);
      setInvoiceSuccess('');
      setInvoiceError('');
    } else {
      setSaleInvoices([]);
    }
  }, [viewingSale, stripeEnabled]);

  // Preview mode for dashboard
  if (previewMode) {
    return (
      <div className="overflow-auto w-full rounded-lg bg-white p-4 shadow ring-1 ring-gray-200">
        <h3 className="text-sm font-medium text-gray-500 mb-3">Recent Sales</h3>
        <ul className="divide-y divide-gray-100">
          {sales.slice(0, 5).map((sale) => (
            <li key={sale.id} className="py-2">
              <p className="text-sm font-medium text-gray-900">
                {getContactName(sale.contactId)}
              </p>
              <p className="text-xs text-gray-500">${calculateTotal(sale.items).toFixed(2)}</p>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales</h1>
          <p className="mt-2 text-sm text-gray-700">
            Track and manage sales transactions with contacts.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => handleOpenForm()}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Create Sale
          </button>
        </div>
      </div>

      {/* Search Bar and Filters */}
      <div className="mt-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              placeholder="Search sales by contact name or sale ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ${
              showFilters || activeFilterCount > 0
                ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5 mr-1" aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1.5 rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 ring-1 ring-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
              <div>
                <label htmlFor="filterStatus" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="filterStatus"
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="filterProduct" className="block text-sm font-medium text-gray-700 mb-1">
                  Product
                </label>
                <select
                  id="filterProduct"
                  value={filterProductId}
                  onChange={(e) => { setFilterProductId(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                >
                  <option value="">All Products</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="filterPlots" className="block text-sm font-medium text-gray-700 mb-1">
                  Plot Association
                </label>
                <select
                  id="filterPlots"
                  value={filterHasPlots}
                  onChange={(e) => { setFilterHasPlots(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                >
                  <option value="">All Sales</option>
                  <option value="true">Has Plot Associations</option>
                  <option value="false">No Plot Associations</option>
                </select>
              </div>
              <div>
                <label htmlFor="filterDateFrom" className="block text-sm font-medium text-gray-700 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  id="filterDateFrom"
                  value={filterDateFrom}
                  onChange={(e) => { setFilterDateFrom(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="filterDateTo" className="block text-sm font-medium text-gray-700 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  id="filterDateTo"
                  value={filterDateTo}
                  onChange={(e) => { setFilterDateTo(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="filterBalance" className="block text-sm font-medium text-gray-700 mb-1">
                  Balance
                </label>
                <select
                  id="filterBalance"
                  value={filterHasUnpaidBalance}
                  onChange={(e) => { setFilterHasUnpaidBalance(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                >
                  <option value="">All Sales</option>
                  <option value="true">Has Unpaid Balance</option>
                  <option value="false">Fully Paid</option>
                </select>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {totalSales > 0 && (
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalSales)} of {totalSales} sales
          </p>
        )}
      </div>

      {/* Sales Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {sales.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow ring-1 ring-gray-200">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No sales</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating a new sale.</p>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => handleOpenForm()}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Create Sale
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Contact
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Items
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('saleTotalAmount')}
                      >
                        Total
                        {renderSortIcon('saleTotalAmount')}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('updatedAt')}
                      >
                        Last Updated
                        {renderSortIcon('updatedAt')}
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sales.map((sale) => (
                      <tr
                        key={sale.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewSale(sale)}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">
                            {getContactName(sale.contactId)}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <ul className="list-disc list-inside">
                            {sale.items?.slice(0, 2).map((item, idx) => (
                              <li key={idx} className="truncate max-w-xs">
                                {item.name} x {item.qty}
                              </li>
                            ))}
                            {sale.items?.length > 2 && (
                              <li className="text-gray-400">+{sale.items.length - 2} more</li>
                            )}
                          </ul>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                          ${calculateTotal(sale.items).toFixed(2)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={sale.status}
                            onChange={(e) => handleStatusChange(sale.id, e.target.value)}
                            className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            {/* Show current status as first option */}
                            <option value={sale.status}>
                              {getStatusName(sale.status)}
                            </option>
                            {/* Only allow manual changes to cancelled or refunded */}
                            {statuses
                              .filter(s => s.canManuallySet && s.value !== sale.status)
                              .map((status) => (
                                <option key={status.value} value={status.value}>
                                  {status.label}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(sale.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenForm(sale)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(sale.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalSales)}</span> of{' '}
                <span className="font-medium">{totalSales}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pageNum === currentPage
                            ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return (
                      <span
                        key={pageNum}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseForm} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {selectedSale ? 'Edit Sale' : 'Create New Sale'}
                </h3>
                <form onSubmit={handleSave} className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsContactModalOpen(true)}
                        className="w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm hover:bg-gray-50 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                      >
                        {selectedContact ? (
                          <span className="flex items-center">
                            <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="font-medium text-gray-900">
                              {selectedContact.firstName} {selectedContact.lastName}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-500">Select a contact...</span>
                        )}
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {!selectedContact && formData.contactId === '' && (
                        <p className="mt-1 text-xs text-gray-500">Click to search and select a contact</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
                        Due Date
                      </label>
                      <input
                        type="date"
                        id="dueDate"
                        name="dueDate"
                        value={formData.dueDate}
                        onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Sales become overdue after this date if not fully paid
                      </p>
                    </div>
                  </div>

                  {/* Add Line Item Section */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Add Line Items</h4>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-5">
                        <select
                          value={lineItem.productId}
                          onChange={(e) => setLineItem({ ...lineItem, productId: e.target.value })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">Select Service</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name} - ${parseFloat(product.price).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          min="1"
                          placeholder="Qty"
                          value={lineItem.qty}
                          onChange={(e) => setLineItem({ ...lineItem, qty: parseInt(e.target.value) || 1 })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="col-span-3">
                        <input
                          type="text"
                          placeholder="Note"
                          value={lineItem.note}
                          onChange={(e) => setLineItem({ ...lineItem, note: e.target.value })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <button
                          type="button"
                          onClick={handleAddItem}
                          className="w-full rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Line Items List */}
                  {formData.items.length > 0 && (
                    <div className="border rounded-md divide-y divide-gray-200">
                      {formData.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between px-4 py-3">
                          <div>
                            <span className="font-medium text-gray-900">{item.name}</span>
                            <span className="text-gray-500"> x {item.qty}</span>
                            <span className="text-gray-500"> @ ${parseFloat(item.price).toFixed(2)}</span>
                            {item.note && <span className="text-gray-400 ml-2">- {item.note}</span>}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-medium text-gray-900">
                              ${(item.price * item.qty).toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                        <span className="font-semibold text-gray-900">Total</span>
                        <span className="font-semibold text-gray-900">
                          ${calculateTotal(formData.items).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Associated Plots Section - only for new sales */}
                  {!selectedSale && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Associated Plots (Optional)</h4>
                        <button
                          type="button"
                          onClick={() => setIsFormPlotModalOpen(true)}
                          className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          <MapPinIcon className="h-4 w-4" />
                          Add Plot
                        </button>
                      </div>
                      {formPlots.length > 0 ? (
                        <div className="border rounded-md divide-y divide-gray-200">
                          {formPlots.map((plot) => (
                            <div key={plot.fid} className="flex items-center justify-between px-4 py-3">
                              <div className="flex items-center">
                                <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="font-medium text-gray-900">
                                  {plot.alias || `Plot ${plot.fid}`}
                                </span>
                                {plot.section && (
                                  <span className="text-gray-500 ml-2">Section: {plot.section}</span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveFormPlot(plot.fid)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No plots associated with this sale yet.</p>
                      )}
                    </div>
                  )}

                  {flexDefs.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Fields</h4>
                      {flexDefs.map(def => (
                        <div key={def.id} className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {def.fieldLabel}
                            {def.isRequired && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          {def.fieldType === 'text' && (
                            <input type="text" value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'number' && (
                            <input type="number" value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'date' && (
                            <input type="date" value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'textarea' && (
                            <textarea value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} rows={2} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'select' && (
                            <select value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500">
                              <option value="">-- Select --</option>
                              {(def.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          )}
                          {def.fieldType === 'checkbox' && (
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={(flexVals[def.id] || '') === 'true'} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: String(e.target.checked) }))} className="h-4 w-4 text-indigo-600 rounded" />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={loading || formData.items.length === 0 || !formData.contactId}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 sm:col-start-2"
                    >
                      {loading ? 'Saving...' : (selectedSale ? 'Save Changes' : 'Record Sale')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sale Details View Modal */}
      {viewingSale && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setViewingSale(null)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
              {/* Close button */}
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={() => setViewingSale(null)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Sale Details
                </h3>

                {/* Sale Summary */}
                <div className="space-y-4">
                  {/* Contact & Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Contact</dt>
                      <dd className="mt-1 text-sm text-gray-900">{getContactName(viewingSale.contactId)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Date</dt>
                      <dd className="mt-1 text-sm text-gray-900">{new Date(viewingSale.createdAt).toLocaleDateString()}</dd>
                    </div>
                  </div>

                  {/* Status and Due Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Current Status</dt>
                      <dd className="mt-1">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                          viewingSale.status === 'paid' ? 'bg-green-100 text-green-800' :
                          viewingSale.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                          viewingSale.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          viewingSale.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                          viewingSale.status === 'refunded' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {getStatusName(viewingSale.status)}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {viewingSale.dueDate ? new Date(viewingSale.dueDate).toLocaleDateString() : 'Not set'}
                        {viewingSale.dueDate && new Date(viewingSale.dueDate) < new Date() && viewingSale.status !== 'paid' && viewingSale.status !== 'cancelled' && viewingSale.status !== 'refunded' && (
                          <span className="ml-2 text-red-600 text-xs font-medium">Overdue</span>
                        )}
                      </dd>
                    </div>
                  </div>

                  {/* Additional Fields */}
                  <FlexFieldValues entityType="sale" entityId={viewingSale.id} />

                  {/* Items */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Items</dt>
                    <dd>
                      <div className="border rounded-md divide-y divide-gray-200">
                        {viewingSale.items?.map((item, index) => (
                          <div key={index} className="flex items-center justify-between px-4 py-3">
                            <div>
                              <span className="font-medium text-gray-900">{item.name}</span>
                              <span className="text-gray-500"> x {item.qty}</span>
                              <span className="text-gray-500"> @ ${parseFloat(item.price).toFixed(2)}</span>
                              {item.note && <span className="text-gray-400 ml-2">- {item.note}</span>}
                            </div>
                            <span className="font-medium text-gray-900">
                              ${(item.price * item.qty).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                          <span className="font-semibold text-gray-900">Total</span>
                          <span className="font-semibold text-gray-900">
                            ${calculateTotal(viewingSale.items).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </dd>
                  </div>

                  {/* Associated Plots */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <dt className="text-sm font-medium text-gray-500">Associated Plots</dt>
                      <button
                        type="button"
                        onClick={() => setIsPlotModalOpen(true)}
                        className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Plot
                      </button>
                    </div>
                    <dd>
                      {salePlots.length > 0 ? (
                        <div className="border rounded-md divide-y divide-gray-200">
                          {salePlots.map((plotSale) => (
                            <div key={plotSale.id} className="flex items-center justify-between px-4 py-3">
                              <div>
                                <span className="font-medium text-gray-900">
                                  {plotSale.PlotPoly?.alias || `Plot ${plotSale.plotId}`}
                                </span>
                                {plotSale.PlotPoly?.section && (
                                  <span className="text-gray-500 ml-2">Section: {plotSale.PlotPoly.section}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleViewPlotOnMap(plotSale.plotId)}
                                  className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900"
                                >
                                  <MapPinIcon className="h-4 w-4 mr-1" />
                                  View on Map
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePlotFromSale(plotSale.id)}
                                  className="inline-flex items-center text-sm text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No plots associated with this sale. Click "Add Plot" to link a plot.</p>
                      )}
                    </dd>
                  </div>

                  {/* Payments Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <dt className="text-sm font-medium text-gray-500">Payments</dt>
                      {calculateRemainingBalance(viewingSale.items, salePayments) > 0 && (
                        <div className="flex items-center gap-2">
                          {stripeEnabled && (
                            <button
                              type="button"
                              onClick={handleSendPaymentInvoice}
                              disabled={sendingInvoice}
                              className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                            >
                              {sendingInvoice ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-1"></div>
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <CreditCardIcon className="h-4 w-4 mr-1" />
                                  Send Payment Invoice
                                </>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleOpenPaymentModal}
                            className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
                          >
                            <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                            Record Payment
                          </button>
                        </div>
                      )}
                    </div>
                    <dd>
                      <div className="border rounded-md divide-y divide-gray-200">
                        {salePayments.length > 0 ? (
                          <>
                            {salePayments.map((payment) => (
                              <div key={payment.id} className="flex items-center justify-between px-4 py-3">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    ${parseFloat(payment.amount).toFixed(2)} - {payment.paymentType}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {new Date(payment.paymentDate).toLocaleDateString()} | {payment.Contact ? `${payment.Contact.firstName} ${payment.Contact.lastName}` : 'Unknown'}
                                  </div>
                                  {payment.note && (
                                    <div className="text-sm text-gray-400 italic">{payment.note}</div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePayment(payment.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Payment"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500 italic">
                            No payments recorded yet.
                          </div>
                        )}
                        {/* Payment Summary */}
                        <div className="px-4 py-3 bg-gray-50">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Sale Total:</span>
                            <span className="font-medium text-gray-900">${calculateTotal(viewingSale.items).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-gray-500">Total Paid:</span>
                            <span className="font-medium text-green-600">${calculateTotalPaid(salePayments).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm mt-1 pt-1 border-t border-gray-200">
                            <span className="font-semibold text-gray-700">Remaining Balance:</span>
                            <span className={`font-semibold ${calculateRemainingBalance(viewingSale.items, salePayments) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ${calculateRemainingBalance(viewingSale.items, salePayments).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </dd>
                  </div>

                  {/* Invoice Status Messages */}
                  {invoiceSuccess && (
                    <div className="rounded-md bg-green-50 p-3">
                      <p className="text-sm text-green-700">{invoiceSuccess}</p>
                    </div>
                  )}
                  {invoiceError && (
                    <div className="rounded-md bg-red-50 p-3">
                      <p className="text-sm text-red-700">{invoiceError}</p>
                    </div>
                  )}

                  {/* Stripe Invoice History */}
                  {stripeEnabled && saleInvoices.length > 0 && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-2">Payment Invoices Sent</dt>
                      <dd>
                        <div className="border rounded-md divide-y divide-gray-200">
                          {saleInvoices.map((inv) => (
                            <div key={inv.id} className="px-4 py-2 flex items-center justify-between">
                              <div>
                                <p className="text-sm text-gray-900">
                                  ${parseFloat(inv.amount).toFixed(2)} - {new Date(inv.createdAt).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {inv.Contact ? `${inv.Contact.firstName} ${inv.Contact.lastName}` : ''}
                                  {inv.emailOpenedAt && (
                                    <span className="ml-2 text-blue-600">Viewed {new Date(inv.emailOpenedAt).toLocaleDateString()}</span>
                                  )}
                                </p>
                              </div>
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                inv.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {inv.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </dd>
                    </div>
                  )}

                  {/* Status History */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Status History</dt>
                    <dd>
                      {formatStatusHistory(viewingSale).length > 0 ? (
                        <div className="overflow-hidden border rounded-md">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Changed By
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {formatStatusHistory(viewingSale).map((entry, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2 text-sm text-gray-900">{entry.status}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">
                                    {entry.date.toLocaleDateString()} {entry.date.toLocaleTimeString()}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500">
                                    {entry.changedBy || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No status changes recorded</p>
                      )}
                    </dd>
                  </div>

                  {/* Documents */}
                  <DocumentGenerationPanel
                    entityType="sale"
                    entityId={viewingSale.id}
                  />
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => handleDelete(viewingSale.id, true)}
                    className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                  >
                    <TrashIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={handleEditFromView}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    <PencilIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Search Modal */}
      <ContactSearchModal
        open={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSelect={handleContactSelect}
        selectedId={formData.contactId ? parseInt(formData.contactId) : null}
      />

      {/* Plot Search Modal for adding plots to existing sale */}
      <PlotSearchModal
        open={isPlotModalOpen}
        onClose={() => setIsPlotModalOpen(false)}
        onSelect={handleAddPlotToSale}
        excludeIds={salePlots.map(ps => ps.plotId)}
      />

      {/* Plot Search Modal for adding plots to new sale form */}
      <PlotSearchModal
        open={isFormPlotModalOpen}
        onClose={() => setIsFormPlotModalOpen(false)}
        onSelect={handleAddFormPlot}
        excludeIds={formPlots.map(p => p.fid)}
      />

      {/* Record Payment Modal */}
      {isPaymentModalOpen && viewingSale && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClosePaymentModal} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleClosePaymentModal}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Record Payment
                </h3>

                {/* Remaining Balance Display */}
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Sale Total:</span>
                    <span className="font-medium">${calculateTotal(viewingSale.items).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">Already Paid:</span>
                    <span className="font-medium text-green-600">${calculateTotalPaid(salePayments).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1 pt-1 border-t border-gray-200">
                    <span className="font-semibold text-gray-700">Remaining Balance:</span>
                    <span className="font-semibold text-red-600">
                      ${calculateRemainingBalance(viewingSale.items, salePayments).toFixed(2)}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleRecordPayment} className="space-y-4">
                  {/* Amount */}
                  <div>
                    <label htmlFor="paymentAmount" className="block text-sm font-medium text-gray-700">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="paymentAmount"
                        step="0.01"
                        min="0.01"
                        max={calculateRemainingBalance(viewingSale.items, salePayments)}
                        value={paymentFormData.amount}
                        onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                        className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum: ${calculateRemainingBalance(viewingSale.items, salePayments).toFixed(2)}
                    </p>
                  </div>

                  {/* Payment Date */}
                  <div>
                    <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700">
                      Payment Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="paymentDate"
                      value={paymentFormData.paymentDate}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentDate: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    />
                  </div>

                  {/* Payment Type */}
                  <div>
                    <label htmlFor="paymentType" className="block text-sm font-medium text-gray-700">
                      Payment Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="paymentType"
                      value={paymentFormData.paymentType}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentType: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    >
                      <option value="Cash">Cash</option>
                      <option value="Check">Check</option>
                      <option value="Card">Card</option>
                    </select>
                  </div>

                  {/* Contact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Paid By <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsPaymentContactModalOpen(true)}
                      className="w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm hover:bg-gray-50 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                    >
                      {selectedPaymentContact ? (
                        <span className="flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">
                            {selectedPaymentContact.firstName} {selectedPaymentContact.lastName}
                          </span>
                        </span>
                      ) : (
                        <span className="text-gray-500">Select contact...</span>
                      )}
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  {/* Note */}
                  <div>
                    <label htmlFor="paymentNote" className="block text-sm font-medium text-gray-700">
                      Note
                    </label>
                    <textarea
                      id="paymentNote"
                      rows={2}
                      value={paymentFormData.note}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, note: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Optional note about this payment..."
                    />
                  </div>

                  {/* Note about auto status */}
                  <div className="p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> Sale status will automatically update based on payment balance.
                    </p>
                  </div>

                  {paymentError && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{paymentError}</p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={paymentLoading || !paymentFormData.amount || !paymentFormData.contactId}
                      className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 sm:col-start-2"
                    >
                      {paymentLoading ? 'Recording...' : 'Record Payment'}
                    </button>
                    <button
                      type="button"
                      onClick={handleClosePaymentModal}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Search Modal for Payment */}
      <ContactSearchModal
        open={isPaymentContactModalOpen}
        onClose={() => setIsPaymentContactModalOpen(false)}
        onSelect={handlePaymentContactSelect}
        selectedId={paymentFormData.contactId ? parseInt(paymentFormData.contactId) : null}
      />
    </div>
  );
};

export default SalesConfig;
